import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Activity, Heart, Moon, Zap, TrendingUp, 
  RefreshCw, ExternalLink, Footprints, Flame,
  Clock, Award, ChevronRight, MessageCircle, Link, Unlink, Watch, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell
} from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// WhatsApp Community Link
const WHATSAPP_COMMUNITY_LINK = "https://chat.whatsapp.com/GtvNjx1xhUb0dJIBXsEY76";

// Supported WeFitter devices
const WEFITTER_DEVICES = [
  { name: 'Fitbit', icon: '⌚', color: '#00B0B9' },
  { name: 'Garmin', icon: '⌚', color: '#007CC3' },
  { name: 'Apple Health', icon: '🍎', color: '#FF3B30' },
  { name: 'Google Fit', icon: '🏃', color: '#4285F4' },
  { name: 'Samsung Health', icon: '📱', color: '#1428A0' },
  { name: 'Polar', icon: '❄️', color: '#D21034' },
  { name: 'Suunto', icon: '🧭', color: '#000000' },
  { name: 'Withings', icon: '💊', color: '#00B5AD' },
  { name: 'Oura', icon: '💍', color: '#C8C8C8' }
];

export default function FitnessDashboard({ childId, childName }) {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [integrationStatus, setIntegrationStatus] = useState(null);
  
  // Data states
  const [stravaActivities, setStravaActivities] = useState(null);
  const [stravaStats, setStravaStats] = useState(null);
  const [fitbitDaily, setFitbitDaily] = useState(null);
  const [fitbitHeartRate, setFitbitHeartRate] = useState(null);
  const [fitbitSleep, setFitbitSleep] = useState(null);
  const [fitbitRecovery, setFitbitRecovery] = useState(null);
  const [fitnessSummary, setFitnessSummary] = useState(null);
  
  // WeFitter states
  const [wefitterProfile, setWefitterProfile] = useState(null);
  const [wefitterData, setWefitterData] = useState(null);
  const [wefitterLoading, setWefitterLoading] = useState(false);
  const [wefitterSyncing, setWefitterSyncing] = useState(false);

  const loadWeFitterStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wefitter/profile/${childId}`);
      setWefitterProfile(response.data);
      
      if (response.data.connected) {
        const dataResponse = await axios.get(`${API}/wefitter/data/${childId}/summary?days=14`);
        setWefitterData(dataResponse.data);
      }
    } catch (error) {
      console.error('Failed to load WeFitter status:', error);
    }
  }, [childId]);

  const loadIntegrationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/fitness/integrations`);
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    }
  }, [childId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [activitiesRes, statsRes, dailyRes, hrRes, sleepRes, recoveryRes, summaryRes] = await Promise.all([
        axios.get(`${API}/children/${childId}/strava/activities?limit=10`),
        axios.get(`${API}/children/${childId}/strava/stats`),
        axios.get(`${API}/children/${childId}/fitbit/daily?days=14`),
        axios.get(`${API}/children/${childId}/fitbit/heart-rate`),
        axios.get(`${API}/children/${childId}/fitbit/sleep?days=7`),
        axios.get(`${API}/children/${childId}/fitbit/recovery`),
        axios.get(`${API}/children/${childId}/fitness/summary`)
      ]);
      
      setStravaActivities(activitiesRes.data);
      setStravaStats(statsRes.data);
      setFitbitDaily(dailyRes.data);
      setFitbitHeartRate(hrRes.data);
      setFitbitSleep(sleepRes.data);
      setFitbitRecovery(recoveryRes.data);
      setFitnessSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load fitness data:', error);
      toast.error('Failed to load fitness data');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
    loadIntegrationStatus();
    loadWeFitterStatus();
  }, [loadData, loadIntegrationStatus, loadWeFitterStatus]);

  const handleConnectWeFitter = async () => {
    setWefitterLoading(true);
    try {
      const response = await axios.post(`${API}/wefitter/profile`, { child_id: childId });
      toast.success('WeFitter profile created! You can now connect wearable devices.');
      setWefitterProfile({ connected: true, profile_id: response.data.profile_id });
      await loadWeFitterStatus();
    } catch (error) {
      toast.error('Failed to create WeFitter profile');
      console.error(error);
    } finally {
      setWefitterLoading(false);
    }
  };

  const handleSyncWeFitter = async () => {
    setWefitterSyncing(true);
    try {
      const response = await axios.post(`${API}/wefitter/sync/${childId}?days_back=30`);
      toast.success(`Synced: ${response.data.synced.daily_summaries} days of data`);
      await loadWeFitterStatus();
    } catch (error) {
      toast.error('Failed to sync WeFitter data');
      console.error(error);
    } finally {
      setWefitterSyncing(false);
    }
  };

  const handleDisconnectWeFitter = async () => {
    if (!window.confirm(`Disconnect WeFitter for ${childName}? This will remove all synced data.`)) return;
    
    try {
      await axios.delete(`${API}/wefitter/profile/${childId}`);
      toast.success('WeFitter disconnected');
      setWefitterProfile(null);
      setWefitterData(null);
    } catch (error) {
      toast.error('Failed to disconnect WeFitter');
    }
  };

  const handleRefresh = async () => {
    setSyncing(true);
    await loadData();
    await loadIntegrationStatus();
    setSyncing(false);
    toast.success('Fitness data refreshed');
  };

  const handleConnectStrava = async () => {
    try {
      const response = await axios.get(`${API}/strava/auth?child_id=${childId}`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to start Strava connection');
    }
  };

  const handleConnectFitbit = async () => {
    try {
      const response = await axios.get(`${API}/fitbit/auth?child_id=${childId}`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to start Fitbit connection');
    }
  };

  const handleDisconnect = async (service) => {
    if (!window.confirm(`Disconnect ${service} for ${childName}?`)) return;
    
    try {
      await axios.delete(`${API}/children/${childId}/fitness/integrations/${service}`);
      toast.success(`${service} disconnected`);
      await loadIntegrationStatus();
    } catch (error) {
      toast.error(`Failed to disconnect ${service}`);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-950/30 border border-zinc-800 p-8 text-center">
        <div className="animate-pulse text-zinc-500 font-mono">Loading fitness data...</div>
      </div>
    );
  }

  const isMockData = stravaActivities?.source === 'mock';
  const summary = fitnessSummary?.summary;
  const recovery = fitbitRecovery?.recovery?.today;
  
  const stravaConnected = integrationStatus?.strava?.connected || integrationStatus?.has_global_fallback?.strava;
  const fitbitConnected = integrationStatus?.fitbit?.connected || integrationStatus?.has_global_fallback?.fitbit;

  // Prepare chart data
  const dailyStepsData = fitbitDaily?.daily_summaries?.map(d => ({
    date: d.date.slice(5),
    steps: d.steps,
    calories: d.calories_out
  })).reverse() || [];

  const sleepData = fitbitSleep?.sleep_data?.map(s => ({
    date: s.date.slice(5),
    duration: (s.duration / 60).toFixed(1),
    score: s.sleep_score,
    deep: s.stages.deep,
    light: s.stages.light,
    rem: s.stages.rem
  })).reverse() || [];

  const hrZonesData = fitbitHeartRate?.heart_rate?.heart_rate_zones?.map(z => ({
    name: z.name,
    value: z.minutes
  })) || [];

  const COLORS = ['#02b5ff', '#02b5ff', '#ff025f', '#ff025f'];

  return (
    <div className="space-y-6" data-testid="fitness-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#02b5ff] via-[#6366f1] to-[#ff025f] rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Activity className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Fitness Dashboard</h2>
              <p className="text-white/80 text-sm">
                {isMockData ? 'Demo Data • Connect devices for real tracking' : 'Live Data'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* WhatsApp Community Button */}
            <a
              href={WHATSAPP_COMMUNITY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#02b5ff] hover:bg-[#02b5ff] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
              data-testid="whatsapp-community-btn"
            >
              <MessageCircle className="h-4 w-4" />
              Community
            </a>
            <Button
              onClick={handleRefresh}
              disabled={syncing}
              className="bg-white/20 hover:bg-white/30 text-white rounded-full"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Connection Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Strava Connection */}
        <div className={`p-4 rounded-lg border ${
          integrationStatus?.strava?.connected 
            ? 'bg-[#02b5ff]/10 border-[#02b5ff]/30' 
            : 'bg-zinc-950/30 border-zinc-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                integrationStatus?.strava?.connected ? 'bg-[#FC4C02]' : 'bg-zinc-800'
              }`}>
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Strava</h3>
                {integrationStatus?.strava?.connected ? (
                  <p className="text-xs text-[#02b5ff]">
                    Connected: {integrationStatus.strava.athlete_name}
                  </p>
                ) : integrationStatus?.has_global_fallback?.strava ? (
                  <p className="text-xs text-[#02b5ff]/70">Using global account</p>
                ) : (
                  <p className="text-xs text-zinc-500">Not connected</p>
                )}
              </div>
            </div>
            {integrationStatus?.strava?.connected ? (
              <Button
                onClick={() => handleDisconnect('strava')}
                size="sm"
                variant="outline"
                className="border-[#ff025f]/30 text-[#ff025f] hover:bg-[#ff025f]/10"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnectStrava}
                size="sm"
                className="bg-[#FC4C02] hover:bg-[#FC4C02]/80 text-white"
              >
                <Link className="h-4 w-4 mr-1" />
                Connect
              </Button>
            )}
          </div>
        </div>

        {/* Fitbit Connection */}
        <div className={`p-4 rounded-lg border ${
          integrationStatus?.fitbit?.connected 
            ? 'bg-[#02b5ff]/10 border-[#02b5ff]/30' 
            : 'bg-zinc-950/30 border-zinc-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                integrationStatus?.fitbit?.connected ? 'bg-[#00B0B9]' : 'bg-zinc-800'
              }`}>
                <Heart className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Fitbit</h3>
                {integrationStatus?.fitbit?.connected ? (
                  <p className="text-xs text-[#02b5ff]">
                    Connected: User {integrationStatus.fitbit.user_id}
                  </p>
                ) : integrationStatus?.has_global_fallback?.fitbit ? (
                  <p className="text-xs text-[#02b5ff]/70">Using global account</p>
                ) : (
                  <p className="text-xs text-zinc-500">Not connected</p>
                )}
              </div>
            </div>
            {integrationStatus?.fitbit?.connected ? (
              <Button
                onClick={() => handleDisconnect('fitbit')}
                size="sm"
                variant="outline"
                className="border-[#ff025f]/30 text-[#ff025f] hover:bg-[#ff025f]/10"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnectFitbit}
                size="sm"
                className="bg-[#00B0B9] hover:bg-[#00B0B9]/80 text-white"
              >
                <Link className="h-4 w-4 mr-1" />
                Connect
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* WeFitter - Universal Wearable Connection */}
      <div className={`p-4 rounded-lg border ${
        wefitterProfile?.connected 
          ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30' 
          : 'bg-zinc-950/30 border-zinc-800'
      }`} data-testid="wefitter-connection">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              wefitterProfile?.connected ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-zinc-800'
            }`}>
              <Watch className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">WeFitter</h3>
              {wefitterProfile?.connected ? (
                <p className="text-xs text-purple-400">
                  Universal wearable sync • {wefitterProfile.connected_sources?.length || 0} devices
                </p>
              ) : (
                <p className="text-xs text-zinc-500">Connect any wearable device</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wefitterProfile?.connected && (
              <Button
                onClick={handleSyncWeFitter}
                disabled={wefitterSyncing}
                size="sm"
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
              >
                {wefitterSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            )}
            {wefitterProfile?.connected ? (
              <Button
                onClick={handleDisconnectWeFitter}
                size="sm"
                variant="outline"
                className="border-[#ff025f]/30 text-[#ff025f] hover:bg-[#ff025f]/10"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnectWeFitter}
                disabled={wefitterLoading}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                {wefitterLoading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Link className="h-4 w-4 mr-1" />
                )}
                Connect
              </Button>
            )}
          </div>
        </div>
        
        {/* Supported devices */}
        {!wefitterProfile?.connected && (
          <div className="flex flex-wrap gap-2 mt-2">
            {WEFITTER_DEVICES.slice(0, 5).map(device => (
              <span key={device.name} className="text-xs px-2 py-1 bg-zinc-800 rounded-full text-zinc-400">
                {device.icon} {device.name}
              </span>
            ))}
            <span className="text-xs px-2 py-1 bg-zinc-800 rounded-full text-zinc-500">+4 more</span>
          </div>
        )}
        
        {/* WeFitter Data Summary */}
        {wefitterProfile?.connected && wefitterData?.summary && (
          <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t border-purple-500/20">
            <div className="text-center">
              <div className="text-lg font-bold text-white font-mono">
                {wefitterData.summary.avg_daily_steps?.toLocaleString() || '-'}
              </div>
              <div className="text-xs text-zinc-500">Avg Steps</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white font-mono">
                {wefitterData.summary.avg_daily_calories?.toLocaleString() || '-'}
              </div>
              <div className="text-xs text-zinc-500">Avg Cal</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white font-mono">
                {wefitterData.heart_rate?.resting_hr || '-'}
              </div>
              <div className="text-xs text-zinc-500">Resting HR</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white font-mono">
                {wefitterData.sleep?.duration_hours || '-'}h
              </div>
              <div className="text-xs text-zinc-500">Sleep</div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <Footprints className="h-4 w-4" />
              AVG STEPS
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.average_daily_steps?.toLocaleString()}
            </div>
          </div>
          
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <Moon className="h-4 w-4" />
              AVG SLEEP
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.average_sleep_hours}h
            </div>
          </div>
          
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <Heart className="h-4 w-4 text-[#ff025f]" />
              RESTING HR
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.resting_heart_rate} <span className="text-sm text-zinc-500">bpm</span>
            </div>
          </div>
          
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <TrendingUp className="h-4 w-4 text-[#02b5ff]" />
              HRV
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.hrv_score} <span className="text-sm text-zinc-500">ms</span>
            </div>
          </div>
          
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <Zap className="h-4 w-4 text-[#02b5ff]" />
              RECOVERY
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.recovery_score}%
            </div>
          </div>
          
          <div className="bg-zinc-950/50 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
              <Flame className="h-4 w-4 text-[#ff025f]" />
              STRAIN
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {summary.health_summary.strain_score}
            </div>
          </div>
        </div>
      )}

      {/* Recovery Status Card */}
      {recovery && (
        <div className={`p-6 rounded-lg border ${
          recovery.readiness === 'high' 
            ? 'bg-[#02b5ff]/10/30 border-[#02b5ff]/50' 
            : recovery.readiness === 'moderate'
            ? 'bg-[#02b5ff]/10/30 border-[#02b5ff]/50'
            : 'bg-[#ff025f]/10/30 border-[#ff025f]/50'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white mb-1">Today's Readiness</h3>
              <p className="text-zinc-400 text-sm">{recovery.recommendations?.[0]}</p>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold font-mono ${
                recovery.readiness === 'high' ? 'text-[#02b5ff]' : 
                recovery.readiness === 'moderate' ? 'text-[#02b5ff]' : 'text-[#ff025f]'
              }`}>
                {recovery.recovery_score}%
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">
                {recovery.readiness} Readiness
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-zinc-800">
        <div className="flex gap-1">
          {['overview', 'activities', 'sleep', 'heart'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wider transition-all border-b-2 ${
                activeTab === tab 
                  ? 'text-[#02b5ff] border-indigo-400' 
                  : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Steps Chart */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Footprints className="h-5 w-5 text-[#02b5ff]" />
                Daily Steps (14 Days)
              </h3>
              <SafeResponsiveContainer width="100%" height={250} minHeight={250}>
                <AreaChart data={dailyStepsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: 11 }} />
                  <YAxis stroke="#71717a" style={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="steps" 
                    stroke="#02b5ff" 
                    fill="#02b5ff" 
                    fillOpacity={0.3}
                    name="Steps"
                  />
                </AreaChart>
              </SafeResponsiveContainer>
            </div>

            {/* Activity Summary */}
            {summary && (
              <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#02b5ff]" />
                  Activity Summary (30 Days)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-900/50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-white font-mono">
                      {summary.activity_summary.total_activities}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase">Activities</div>
                  </div>
                  <div className="bg-zinc-900/50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-white font-mono">
                      {summary.activity_summary.total_distance_km}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase">KM Total</div>
                  </div>
                  <div className="bg-zinc-900/50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-white font-mono">
                      {summary.activity_summary.total_duration_hours}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase">Hours</div>
                  </div>
                  <div className="bg-zinc-900/50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-white font-mono">
                      {summary.activity_summary.total_calories_burned?.toLocaleString()}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase">Calories</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-zinc-500">
                  Most Active: <span className="text-white font-medium">{summary.activity_summary.most_common_activity}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && stravaActivities && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Recent Activities</h3>
              {stravaStats && (
                <div className="text-sm text-zinc-500">
                  Personal Best 5K: <span className="text-[#02b5ff] font-mono">{stravaStats.personal_bests?.fastest_5k}</span>
                </div>
              )}
            </div>
            
            {stravaActivities.activities?.map((activity, idx) => (
              <div 
                key={idx} 
                className="bg-zinc-950/30 border border-zinc-800 p-4 rounded-lg hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        activity.type === 'Run' ? 'bg-[#02b5ff]/20/50 text-[#02b5ff]' :
                        activity.type === 'Ride' ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                        activity.type === 'Swim' ? 'bg-cyan-900/50 text-[#02b5ff]' :
                        'bg-purple-900/50 text-[#02b5ff]'
                      }`}>
                        {activity.type}
                      </span>
                      <span className="text-zinc-400 text-sm">
                        {new Date(activity.start_date).toLocaleDateString('en-GB', {
                          weekday: 'short', day: 'numeric', month: 'short'
                        })}
                      </span>
                    </div>
                    <h4 className="text-white font-medium mb-2">{activity.name}</h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-zinc-500">Distance</div>
                        <div className="text-white font-mono">{(activity.distance / 1000).toFixed(2)} km</div>
                      </div>
                      <div>
                        <div className="text-zinc-500">Duration</div>
                        <div className="text-white font-mono">
                          {Math.floor(activity.moving_time / 60)}:{(activity.moving_time % 60).toString().padStart(2, '0')}
                        </div>
                      </div>
                      <div>
                        <div className="text-zinc-500">Avg HR</div>
                        <div className="text-white font-mono">{activity.average_heartrate} bpm</div>
                      </div>
                      <div>
                        <div className="text-zinc-500">Calories</div>
                        <div className="text-white font-mono">{activity.calories}</div>
                      </div>
                    </div>
                  </div>
                  {activity.suffer_score && (
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#ff025f] font-mono">{activity.suffer_score}</div>
                      <div className="text-xs text-zinc-500">Effort</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sleep Tab */}
        {activeTab === 'sleep' && fitbitSleep && (
          <div className="space-y-6">
            {/* Sleep Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-zinc-950/30 border border-zinc-800 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-white font-mono">
                  {fitbitSleep.sleep_summary?.avg_duration_hours}h
                </div>
                <div className="text-xs text-zinc-500 uppercase">Avg Duration</div>
              </div>
              <div className="bg-zinc-950/30 border border-zinc-800 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-white font-mono">
                  {fitbitSleep.sleep_summary?.avg_efficiency}%
                </div>
                <div className="text-xs text-zinc-500 uppercase">Avg Efficiency</div>
              </div>
              <div className="bg-zinc-950/30 border border-zinc-800 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-white font-mono">
                  {fitbitSleep.sleep_summary?.avg_sleep_score}
                </div>
                <div className="text-xs text-zinc-500 uppercase">Avg Score</div>
              </div>
            </div>

            {/* Sleep Duration Chart */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Moon className="h-5 w-5 text-[#02b5ff]" />
                Sleep Duration & Score (7 Days)
              </h3>
              <SafeResponsiveContainer width="100%" height={250} minHeight={250}>
                <BarChart data={sleepData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" stroke="#71717a" style={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#71717a" style={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                  <Bar yAxisId="left" dataKey="duration" fill="#02b5ff" name="Duration (h)" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="score" stroke="#02b5ff" name="Score" strokeWidth={2} />
                </BarChart>
              </SafeResponsiveContainer>
            </div>

            {/* Sleep Stages */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-white mb-4">Recent Sleep Breakdown</h3>
              <div className="space-y-3">
                {fitbitSleep.sleep_data?.slice(0, 5).map((night, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-20 text-sm text-zinc-500">{night.date.slice(5)}</div>
                    <div className="flex-1 flex h-6 rounded overflow-hidden">
                      <div 
                        className="bg-[#02b5ff]" 
                        style={{ width: `${(night.stages.deep / night.duration) * 100}%` }}
                        title={`Deep: ${night.stages.deep}min`}
                      />
                      <div 
                        className="bg-[#02b5ff]" 
                        style={{ width: `${(night.stages.light / night.duration) * 100}%` }}
                        title={`Light: ${night.stages.light}min`}
                      />
                      <div 
                        className="bg-cyan-400" 
                        style={{ width: `${(night.stages.rem / night.duration) * 100}%` }}
                        title={`REM: ${night.stages.rem}min`}
                      />
                      <div 
                        className="bg-zinc-600" 
                        style={{ width: `${(night.stages.wake / night.duration) * 100}%` }}
                        title={`Awake: ${night.stages.wake}min`}
                      />
                    </div>
                    <div className="w-16 text-right text-white font-mono text-sm">
                      {(night.duration / 60).toFixed(1)}h
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-4 text-xs">
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#02b5ff] rounded" /> Deep</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#02b5ff] rounded" /> Light</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-cyan-400 rounded" /> REM</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-zinc-600 rounded" /> Awake</div>
              </div>
            </div>
          </div>
        )}

        {/* Heart Rate Tab */}
        {activeTab === 'heart' && fitbitHeartRate && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* HR Summary */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-[#ff025f]" />
                Heart Rate Summary
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-900/50 p-4 rounded-lg text-center">
                  <div className="text-4xl font-bold text-[#ff025f] font-mono">
                    {fitbitHeartRate.heart_rate?.resting_heart_rate}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase mt-1">Resting HR (bpm)</div>
                </div>
                <div className="bg-zinc-900/50 p-4 rounded-lg text-center">
                  <div className="text-4xl font-bold text-[#02b5ff] font-mono">
                    {fitbitHeartRate.heart_rate?.hrv?.daily_rmssd}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase mt-1">HRV (ms)</div>
                </div>
              </div>
              
              {/* HRV Insight */}
              <div className="mt-4 p-4 bg-purple-950/30 border border-purple-800 rounded-lg">
                <div className="text-sm text-purple-200">
                  <strong>HRV Insight:</strong> Higher HRV generally indicates better recovery and cardiovascular fitness. 
                  Athletes typically have HRV values between 50-100ms.
                </div>
              </div>
            </div>

            {/* HR Zones Pie Chart */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-white mb-4">Heart Rate Zones (Today)</h3>
              <div className="flex items-center">
                <SafeResponsiveContainer width="50%" height={200} minHeight={200}>
                  <PieChart>
                    <Pie
                      data={hrZonesData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                    >
                      {hrZonesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                    />
                  </PieChart>
                </SafeResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {fitbitHeartRate.heart_rate?.heart_rate_zones?.map((zone, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[idx] }} />
                        <span className="text-zinc-400">{zone.name}</span>
                      </div>
                      <span className="text-white font-mono">{zone.minutes} min</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Intraday HR Chart */}
            <div className="bg-zinc-950/30 border border-zinc-800 p-6 rounded-lg lg:col-span-2">
              <h3 className="text-lg font-bold text-white mb-4">Heart Rate Throughout Day</h3>
              <SafeResponsiveContainer width="100%" height={200} minHeight={200}>
                <AreaChart data={fitbitHeartRate.heart_rate?.intraday || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="time" stroke="#71717a" style={{ fontSize: 11 }} />
                  <YAxis stroke="#71717a" style={{ fontSize: 11 }} domain={[40, 180]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#ff025f" 
                    fill="#ff025f" 
                    fillOpacity={0.3}
                    name="Heart Rate"
                  />
                </AreaChart>
              </SafeResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Mock Data Notice */}
      {isMockData && !stravaConnected && !fitbitConnected && (
        <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/50 rounded-lg p-4 flex items-start gap-3">
          <div className="text-[#02b5ff] mt-0.5">ℹ️</div>
          <div>
            <div className="text-[#02b5ff] font-medium">Demo Mode</div>
            <div className="text-[#02b5ff]/70 text-sm">
              This is sample data for demonstration. Use the "Connect" buttons above to link {childName}'s Strava and Fitbit accounts for real fitness metrics.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
